"use client";

import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PageHero from "@/components/sections/PageHero";
import Link from "next/link";
import { useLanguage } from "@/lib/i18n";

export default function SitemapPage() {
  const { language } = useLanguage();

  const content = language === "id" ? {
    title: "Peta Situs",
    subtitle: "Struktur lengkap website KinKen Kost Elite untuk navigasi yang mudah",
    superText: "Navigation Guide",
    structureTitle: "Struktur Website Lengkap",
    structureSubtitle: "Temukan semua halaman dan konten premium kami dengan mudah",
    sections: [
      {
        title: "Navigasi Utama",
        links: [
          { name: "Beranda", href: "/", desc: "Pengalaman luxury living di Bali" },
          { name: "Tentang Kami", href: "/about", desc: "Filosofi & nilai-nilai kami" },
          { name: "Kamar", href: "/rooms", desc: "Tipe kamar premium & harga" },
          { name: "Fasilitas", href: "/facilities", desc: "Amenitas mewah" },
          { name: "Galeri", href: "/gallery", desc: "Pengalaman visual" },
          { name: "Lokasi", href: "/location", desc: "Lokasi premium di Renon" },
          { name: "Kontak", href: "/contact", desc: "Hubungi kami" },
        ],
      },
      {
        title: "Dukungan & Informasi",
        links: [
          { name: "FAQ", href: "/faq", desc: "Pertanyaan yang sering diajukan" },
          { name: "Testimoni", href: "/#testimonials", desc: "Pengalaman tamu" },
          { name: "Kebijakan Privasi", href: "/privacy-policy", desc: "Perlindungan data" },
          { name: "Syarat & Ketentuan", href: "/terms-of-service", desc: "Aturan & regulasi" },
          { name: "Peta Situs", href: "/sitemap", desc: "Halaman ini" },
        ],
      },
    ],
    helpTitle: "Butuh Bantuan Navigasi?",
    helpDesc: "Jika Anda mengalami kesulitan menemukan informasi tertentu, tim kami siap membantu.",
    contactConcierge: "Hubungi Concierge Kami",
  } : {
    title: "Website Sitemap",
    subtitle: "Complete structure of KinKen Kost Elite website for easy navigation",
    superText: "Navigation Guide",
    structureTitle: "Complete Website Structure",
    structureSubtitle: "Find all our pages and premium content easily",
    sections: [
      {
        title: "Main Navigation",
        links: [
          { name: "Home", href: "/", desc: "Luxury living experience in Bali" },
          { name: "About Us", href: "/about", desc: "Our philosophy & values" },
          { name: "Rooms", href: "/rooms", desc: "Premium room types & pricing" },
          { name: "Facilities", href: "/facilities", desc: "Luxury amenities" },
          { name: "Gallery", href: "/gallery", desc: "Visual experience" },
          { name: "Location", href: "/location", desc: "Prime Renon location" },
          { name: "Contact", href: "/contact", desc: "Get in touch" },
        ],
      },
      {
        title: "Support & Information",
        links: [
          { name: "FAQ", href: "/faq", desc: "Frequently asked questions" },
          { name: "Testimonials", href: "/#testimonials", desc: "Guest experiences" },
          { name: "Privacy Policy", href: "/privacy-policy", desc: "Data protection" },
          { name: "Terms of Service", href: "/terms-of-service", desc: "Rules & regulations" },
          { name: "Sitemap", href: "/sitemap", desc: "This page" },
        ],
      },
    ],
    helpTitle: "Need Help Navigating?",
    helpDesc: "If you have difficulty finding certain information, our team is ready to help.",
    contactConcierge: "Contact Our Concierge",
  };

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        title={content.title}
        subtitle={content.subtitle}
        image="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070&auto=format&fit=crop"
        superText={content.superText}
      />

      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-brand-charcoal mb-4">
              {content.structureTitle}
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              {content.structureSubtitle}
            </p>
          </div>

          <div className="space-y-8">
            {content.sections.map((section, idx) => (
              <div key={idx} className="border-b pb-8">
                <h3 className="font-serif text-2xl text-brand-charcoal mb-6">
                  {section.title}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {section.links.map((link, linkIdx) => (
                    <Link
                      key={linkIdx}
                      href={link.href}
                      className="group p-4 hover:bg-gray-50 rounded-lg transition"
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-2 h-2 mt-2 mr-3 bg-brand-gold rounded-full"></div>
                        <div>
                          <h4 className="text-brand-charcoal font-medium group-hover:text-brand-gold transition">
                            {link.name}
                          </h4>
                          <p className="text-gray-500 text-sm mt-1">
                            {link.desc}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <h4 className="font-serif text-xl text-brand-charcoal mb-4">
              {content.helpTitle}
            </h4>
            <p className="text-gray-500 mb-4">
              {content.helpDesc}
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center text-brand-gold hover:text-[#7A6B4D] font-medium"
            >
              {content.contactConcierge} →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
